import { Component, effect, ElementRef, HostListener, inject, input, isDevMode, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseComponent } from '../base.component';
import { ItemDataService } from '../../services/item-data.service';
import { SimulatorService } from '../../services/simulator.service';
import { ItemLibraryService } from '../../services/item-library.service';
import { FilterService } from '../../services/filter.service';
import { GenerationHelperService } from '../../services/generators/generation-helper.service';
import { GameItemParserService } from '../../services/game-item-parser.service';
import { DropPreviewService } from '../../services/drop-preview.service';
import { isJewelleryItemClass } from '../../services/tiering/jewellery-tier.service';
import { GameItem } from '../../models/game-item.models';
import { ArmourType, DefenceType, Rarity, WeaponType } from '../../models/enums';
import { ItemData, JewelleryTierType } from '../../models/item-data-models';
import { PreviewCustomRule } from '../../models/filter-models';
import { SimulatorResult } from '../../models/simulator-models';
import { RulePreviewComponent } from '../rule-preview/rule-preview.component';
import { SelectComponent, SelectOptionComponent } from '../select/select.component';
import { SelectRarityComponent } from '../select/select-rarity/select-rarity.component';
import { SimulatorResultSummaryComponent } from '../simulator-result-summary/simulator-result-summary.component';
import { getGameItemExampleName, getGameItemSocketsClipboard, getGameItemSocketsDisplay } from '../../services/game-item-display.util';
import { socketGroupKeyDown } from '../helper';
import { FilterTitleComponent } from '../filter-title/filter-title.component';

@Component({
  selector: 'app-item-designer-dialog',
  imports: [FormsModule, RulePreviewComponent, SelectComponent, SelectOptionComponent, SelectRarityComponent, SimulatorResultSummaryComponent, FilterTitleComponent],
  templateUrl: './item-designer-dialog.component.html',
  styleUrls: ['../dialogs.scss', './item-designer-dialog.component.scss'],
})
export class ItemDesignerDialogComponent extends BaseComponent {
  initialItem = input<GameItem | undefined>(undefined);
  close = output<void>();

  readonly isDevMode = isDevMode();

  private readonly itemDataService = inject(ItemDataService);
  private readonly simulatorService = inject(SimulatorService);
  private readonly itemLibraryService = inject(ItemLibraryService);
  private readonly filterService = inject(FilterService);
  private readonly generationHelper = inject(GenerationHelperService);
  private readonly gameItemParserService = inject(GameItemParserService);
  private readonly dropPreviewService = inject(DropPreviewService);
  private readonly elementRef = inject(ElementRef);

  itemData: ItemData[];
  cantHaveQuality: string[];
  cantHaveSockets: string[];
  canBeIdentified: string[];
  canHaveUnidentifiedTier: string[];
  canHaveInfluence: string[];
  influences: { name: string; image: string }[];
  transfiguredGems: string[];
  gemQualityTypes = ['Superior', 'Anomalous', 'Divergent', 'Phantasmal'];

  // --- Form state (plain properties for [(ngModel)] compatibility) ---
  selectedItemType = '';
  selectedBaseType = '';
  rarity = Rarity.Normal;
  itemLevel: number | undefined = undefined;
  quality: number | undefined = undefined;
  corrupted: boolean | undefined = undefined;
  stackSize: number | undefined = undefined;

  identified: boolean | undefined = undefined;
  rareName = '';
  explicitMods: string[] = [];
  explicitModNames: string[] = [];
  implicitModCount: number | undefined = undefined;
  unidentifiedItemTier: number | undefined = undefined;

  sockets: number | undefined = undefined;
  linkedSockets: number | undefined = undefined;
  socketGroup = '';
  memoryStrands: number | undefined = undefined;
  socketGroupKeyDown = socketGroupKeyDown;

  hasInfluence = false;
  selectedInfluence = 'Shaper';
  fracturedItem = false;
  synthesisedItem = false; // preserved from pasted items; no UI toggle
  shaperItem = false;
  elderItem = false;
  mirrored = false;
  replica = false;

  waystoneTier: number | undefined = undefined;
  anyEnchantment = false;
  blightedMap = false;
  uberBlightedMap = false;
  elderMap = false;
  shapedMap = false;
  zanaMemory = false;

  gemLevel: number | undefined = undefined;
  gemQualityType = 'Superior';
  transfiguredGem = '';

  enchantmentPassiveNum: number | undefined = undefined;

  modOptions: { key: string; text: string; name: string }[] = [];
  modPickerValue: string | undefined = undefined;

  // --- Derived / computed state ---
  selectedItemData: ItemData | undefined = undefined;
  selectedItemClass = '';
  baseTypeOptions: { name: string; image?: string; gearTier?: string; jewelleryTier?: JewelleryTierType }[] = [];
  selectedDefenceType = '';

  showDefenceType = false;
  showRarity = false;
  showItemLevel = false;
  showQuality = false;
  showCorrupted = false;
  showStackSize = false;
  showIdentification = false;
  showRareName = false;
  showUnidentifiedTier = false;
  showSockets = false;
  showPoe1Equipment = false;
  showWaystone = false;
  showGem = false;
  showGemQualityType = false;
  showClusterJewel = false;
  canAddToLibrary = false;
  maxSockets = 6;
  maxItemLevel = this.poe1 ? 86 : 82;

  simResult: SimulatorResult | undefined;
  debugItemJson = '';

  previewRule: PreviewCustomRule = new PreviewCustomRule({
    exampleName: 'Design an item to preview',
    itemClass: '',
    displayOptions: { active: false, hidden: false, cosmetics: {} } as any,
  });

  constructor() {
    super();
    this.itemData = this.itemDataService.getItemData();
    this.cantHaveQuality = this.itemDataService.getCantHaveQuality();
    this.cantHaveSockets = this.itemDataService.getCantHaveSockets();
    this.canBeIdentified = this.itemDataService.getCanBeIdentified();
    this.canHaveUnidentifiedTier = this.itemDataService.getAllWeaponTypes(true)
      .concat(this.itemDataService.getAllArmourTypes())
      .concat(['Belts', 'Amulets', 'Rings', 'Life Flasks', 'Mana Flasks', 'Charms', 'Jewels']);
    this.canHaveInfluence = this.itemDataService.getAllArmourTypes()
      .concat(this.itemDataService.getAllWeaponTypes())
      .concat(['Belts', 'Amulets', 'Rings']);
    this.influences = this.itemDataService.getInfluences();
    this.transfiguredGems = this.itemDataService.getTransfiguredGems();

    effect(() => {
      this.itemDataService.getModData()();
      this.buildModOptions();
    });

    effect(() => {
      this.filterService.filterResultData();
      this.dropPreviewService.areaLevel();
      this.updatePreview();
    });

    effect(() => {
      const item = this.initialItem();
      if (item) {
        this.populateFromItem(item);
      }
    });
  }

  onItemTypeChange(): void {
    this.selectedItemData = this.itemData.find(d => d.itemType === this.selectedItemType);
    this.selectedItemClass = this.selectedItemData?.itemClass ?? '';
    this.selectedBaseType = '';
    this.selectedDefenceType = '';

    this.computeBaseTypeOptions();

    if (this.baseTypeOptions.length > 0) {
      this.selectedBaseType = this.baseTypeOptions[0].name;
    }

    this.resetItemProperties();
    this.recomputeVisibility();
    this.updatePreview();
  }

  onDefenceTypeChange(): void {
    this.selectedBaseType = '';
    this.computeBaseTypeOptions();
    if (this.baseTypeOptions.length > 0) {
      this.selectedBaseType = this.baseTypeOptions[0].name;
    }
    this.updatePreview();
  }

  onBaseTypeChange(value: string): void {
    this.selectedBaseType = value ?? '';
    this.updatePreview();
  }

  onModPicked(key: string | undefined): void {
    if (key === undefined) return;
    const mod = this.modOptions.find(m => m.key === key);
    if (mod && !this.explicitMods.includes(mod.text)) {
      this.explicitMods = [...this.explicitMods, mod.text];
      this.explicitModNames = [...this.explicitModNames, mod.name];
      this.syncRarityFromExplicitMods();
      this.recomputeVisibility();
      this.updatePreview();
    }
    this.modPickerValue = undefined;
  }

  removeExplicitMod(index: number): void {
    this.explicitMods = this.explicitMods.filter((_, i) => i !== index);
    this.explicitModNames = this.explicitModNames.filter((_, i) => i !== index);
    this.syncRarityFromExplicitMods();
    this.recomputeVisibility();
    this.updatePreview();
  }

  buildItem(): GameItem {
    const isCurrency = this.itemDataService.isDesignerCurrencyItem(this.selectedItemData);
    const noIlvlClasses = ['Stackable Currency', 'Uncut Skill Gems', 'Uncut Support Gems', 'Uncut Spirit Gems',
      'Skill Gems', 'Support Gems'];
    const isGem = this.showGem;

    const props: Partial<GameItem> = {
      itemClass: this.selectedItemClass,
      baseType: this.selectedBaseType,
      rarity: isCurrency ? 'Currency' : this.rarity,
    };

    if (this.showItemLevel && this.itemLevel !== undefined) props.itemLevel = this.itemLevel;
    if (this.showQuality && this.quality !== undefined) props.quality = this.quality;
    if (this.showCorrupted && this.corrupted !== undefined) props.corrupted = this.corrupted;
    if (this.showStackSize && this.stackSize !== undefined) props.stackSize = this.stackSize;

    if (this.showIdentification) {
      if (this.identified !== undefined) props.identified = this.identified;
      if (this.showRareName && this.rareName.trim()) props.name = this.rareName.trim();
      if (this.explicitMods.length > 0) {
        props.explicitMods = [...this.explicitMods];
        props.explicitModNames = [...this.explicitModNames];
      }
      if (this.implicitModCount !== undefined && this.implicitModCount > 0) {
        props.implicitMods = Array(this.implicitModCount).fill('impl');
      }
    }
    if (this.showUnidentifiedTier && this.unidentifiedItemTier !== undefined) {
      props.unidentifiedItemTier = this.unidentifiedItemTier;
    }

    if (this.showSockets) {
      if (this.poe1) {
        const socketGroup = this.normalizeDesignerSocketGroup(this.socketGroup);
        if (socketGroup) {
          props.socketGroup = socketGroup;
          props.linkedSockets = socketGroup.length;
          props.sockets = this.sockets !== undefined
            ? Math.max(this.sockets, socketGroup.length)
            : socketGroup.length;
        } else {
          if (this.linkedSockets !== undefined) {
            props.linkedSockets = this.linkedSockets;
            props.sockets = this.sockets !== undefined
              ? Math.max(this.sockets, this.linkedSockets)
              : this.linkedSockets;
          } else if (this.sockets !== undefined) {
            props.sockets = this.sockets;
          }
        }
        if (this.memoryStrands !== undefined) props.memoryStrands = this.memoryStrands;
      } else if (this.sockets !== undefined) {
        props.sockets = this.sockets;
      }
    }

    if (this.showPoe1Equipment) {
      if (this.hasInfluence) {
        props.hasInfluence = this.selectedInfluence;
      }
      if (this.fracturedItem) props.fracturedItem = true;
      if (this.synthesisedItem) props.synthesisedItem = true;
      if (this.shaperItem) props.shaperItem = true;
      if (this.elderItem) props.elderItem = true;
      if (this.mirrored) props.mirrored = true;
      if (this.replica) props.replica = true;
    }

    if (this.showWaystone) {
      if (this.waystoneTier !== undefined) {
        if (this.selectedItemClass === 'Waystones') props.waystoneTier = this.waystoneTier;
        else props.mapTier = this.waystoneTier;
      }
      if (this.anyEnchantment) props.anyEnchantment = true;
      if (this.poe1) {
        if (this.blightedMap) props.blightedMap = true;
        if (this.uberBlightedMap) props.uberBlightedMap = true;
        if (this.elderMap) props.elderMap = true;
        if (this.shapedMap) props.shapedMap = true;
        if (this.zanaMemory) props.zanaMemory = true;
      }
    }

    if (this.showGem) {
      if (this.gemLevel !== undefined) props.gemLevel = this.gemLevel;
      if (this.showGemQualityType && this.gemQualityType && this.gemQualityType !== 'Superior') {
        props.gemQualityType = this.gemQualityType;
        props.alternateQuality = true;
      }
      if (this.poe1 && this.transfiguredGem) props.transfiguredGem = this.transfiguredGem;
    }

    if (this.showClusterJewel && this.enchantmentPassiveNum !== undefined) {
      props.enchantmentPassiveNum = this.enchantmentPassiveNum;
    }

    const item = new GameItem(props);
    this.gameItemParserService.enrichFromBaseData(item);
    return item;
  }

  recomputeVisibilityAndPreview(): void {
    this.recomputeVisibility();
    this.updatePreview();
  }

  updatePreview(): void {
    if (!this.selectedItemClass || !this.selectedBaseType) {
      this.simResult = undefined;
      this.debugItemJson = '';
      this.previewRule = new PreviewCustomRule({
        exampleName: 'Design an item to preview',
        itemClass: '',
        displayOptions: { active: false, hidden: false, cosmetics: {} } as any,
      });
      return;
    }

    const item = this.buildItem();
    if (this.isDevMode) {
      this.debugItemJson = JSON.stringify(item, null, 2);
    }
    const sim = this.simulatorService.simulate(item, undefined, this.dropPreviewService.areaLevel());
    this.simResult = sim;

    if (!sim.displayOptions) {
      this.previewRule = new PreviewCustomRule({
        exampleName: this.selectedBaseType,
        itemClass: this.selectedItemClass,
        displayOptions: { active: false, hidden: false, cosmetics: {} } as any,
      });
      return;
    }

    const isRareOrIdentifiedUnique =
      item.rarity === Rarity.Rare || (item.rarity === Rarity.Unique && item.identified !== false);

    let socketsStr: string | undefined;
    if (this.showSockets && (item.sockets !== undefined || item.linkedSockets !== undefined || item.socketGroup)) {
      socketsStr = getGameItemSocketsDisplay(item);
    }

    this.previewRule = new PreviewCustomRule({
      displayOptions: sim.displayOptions,
      itemClass: this.selectedItemClass,
      exampleName: getGameItemExampleName(item),
      rareName: isRareOrIdentifiedUnique ? item.name : undefined,
      rarity: item.rarity !== 'Currency' ? (item.rarity as Rarity) : Rarity.Normal,
      isCurrency: this.itemDataService.isDesignerCurrencyItem(this.selectedItemData),
      stackSize: item.stackSize,
      sockets: socketsStr,
      influence: item.hasInfluence,
      memoryStrands: item.memoryStrands ? true : false,
      fractured: item.fracturedItem,
      synthesised: item.synthesisedItem,
      isWaystone: item.itemClass === 'Waystones' || item.itemClass === 'Maps',
      isPoe1Gem: this.poe1 && (item.itemClass === 'Skill Gems' || item.itemClass === 'Support Gems'),
      unidentifiedItemTier: item.unidentifiedItemTier,
    });
  }

  async onAddToLibrary(): Promise<void> {
    if (!this.canAddToLibrary) return;
    const item = this.buildItem();
    const text = this.generateItemText(item);
    await this.itemLibraryService.add(item, text);
    this.close.emit();
  }

  onCancel(): void {
    this.close.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const dialogElement = this.elementRef.nativeElement.querySelector('.dialog');
    if (dialogElement && !dialogElement.contains(event.target as Node)) {
      this.onCancel();
    }
  }

  private recomputeVisibility(): void {
    const cls = this.selectedItemClass;
    const isCurrency = this.itemDataService.isDesignerCurrencyItem(this.selectedItemData);
    const noIlvlClasses = ['Stackable Currency', 'Uncut Skill Gems', 'Uncut Support Gems', 'Uncut Spirit Gems',
      'Skill Gems', 'Support Gems'];

    this.showDefenceType = !!cls && !!this.selectedItemData?.isArmour;
    this.showRarity = !!cls && !isCurrency;
    this.showItemLevel = !!cls && !isCurrency && !noIlvlClasses.includes(cls);
    this.showQuality = !!cls && !this.cantHaveQuality.includes(cls);
    this.showCorrupted = !!cls && !isCurrency;
    this.showStackSize = isCurrency;
    this.showIdentification = !!cls && this.canBeIdentified.includes(cls);
    this.showRareName = this.showIdentification && this.identified === true && this.rarity === Rarity.Rare;
    this.showUnidentifiedTier = this.poe2 && this.showIdentification
      && this.identified === false && this.canHaveUnidentifiedTier.includes(cls);
    this.showSockets = !!cls && !this.cantHaveSockets.includes(cls);
    if (this.showSockets) {
      const twoHandedTypes = this.itemDataService.getTwoHandedWeaponTypes();
      const isTwoHanded = twoHandedTypes.includes(cls as any);
      const isBodyArmour = cls === 'Body Armours';
      if (this.poe2) {
        this.maxSockets = (isTwoHanded || isBodyArmour) ? 4 : 3;
      } else {
        const isSmallArmour = ['Helmets', 'Gloves', 'Boots'].includes(cls);
        this.maxSockets = (isTwoHanded || isBodyArmour) ? 6 : isSmallArmour ? 4 : 3;
      }
    }
    this.showPoe1Equipment = this.poe1 && !!cls && this.canHaveInfluence.includes(cls);
    this.showWaystone = cls === 'Waystones' || cls === 'Maps';
    this.showGem = ['Skill Gems', 'Support Gems', 'Uncut Skill Gems', 'Uncut Support Gems', 'Uncut Spirit Gems'].includes(cls);
    this.showGemQualityType = this.poe1 && ['Skill Gems', 'Support Gems'].includes(cls);
    this.showClusterJewel = this.poe1 && cls === 'Jewels';
    this.canAddToLibrary = !!cls && !!this.selectedBaseType;
  }

  private computeBaseTypeOptions(): void {
    if (!this.selectedItemData) {
      this.baseTypeOptions = [];
      return;
    }

    if (this.selectedItemData.isGear) {
      const weaponType = this.selectedItemData.isWeapon ? [this.selectedItemClass as WeaponType] : undefined;
      const armourType = this.selectedItemData.isArmour ? [this.selectedItemClass as ArmourType] : undefined;
      const defenceTypes = this.selectedDefenceType ? [this.selectedDefenceType as DefenceType] : undefined;
      const options = this.generationHelper.getMyGearBaseTypeAutocompleteOptions(weaponType, armourType, defenceTypes);
      if (options.length > 0) {
        this.baseTypeOptions = options.map(o => ({ name: o.name, image: o.image, gearTier: o.gearTier }));
        return;
      }
    }

    if (isJewelleryItemClass(this.selectedItemData.itemClass)) {
      const options = this.generationHelper.getMyJewelleryBaseTypeAutocompleteOptions(this.selectedItemClass);
      if (options.length > 0) {
        this.baseTypeOptions = options.map(o => ({ name: o.name, image: o.image, jewelleryTier: o.jewelleryTier }));
        return;
      }
    }

    this.baseTypeOptions = this.selectedItemData.baseTypes.map(bt => ({ name: bt.name, image: bt.image }));
  }

  private generateItemText(item: GameItem): string {
    const isCurrency = this.itemDataService.isDesignerCurrencyItem(this.selectedItemData)
      || item.rarity === 'Currency';
    const lines: string[] = [
      `Item Class: ${item.itemClass}`,
      `Rarity: ${isCurrency ? 'Currency' : (item.rarity ?? 'Normal')}`,
    ];
    if (item.rarity === Rarity.Rare && item.name?.trim()) {
      lines.push(item.name.trim());
    }
    lines.push(item.baseType, '--------');
    if (item.itemLevel !== undefined) lines.push(`Item Level: ${item.itemLevel}`);
    if (item.quality) lines.push(`Quality: +${item.quality}%`);
    const socketsClipboard = getGameItemSocketsClipboard(item, this.poe2);
    if (socketsClipboard) {
      lines.push(`Sockets: ${socketsClipboard}`);
    }
    if (item.waystoneTier !== undefined) lines.push(`Waystone Tier: ${item.waystoneTier}`);
    if (item.mapTier !== undefined) lines.push(`Map Tier: ${item.mapTier}`);
    if (item.gemLevel !== undefined) lines.push(`Level: ${item.gemLevel}`);
    if (item.stackSize !== undefined) lines.push(`Stack Size: ${item.stackSize}/1`);
    if (item.corrupted) lines.push('Corrupted');
    if (item.identified === false) lines.push('Unidentified');
    return lines.join('\n');
  }

  private populateFromItem(item: GameItem): void {
    const flat = this.itemDataService.getFlatItemDataByBaseTypeName(item.baseType);
    let itemType = flat?.itemType;
    if (!itemType) {
      itemType = this.itemData.find(d => d.itemClass === item.itemClass)?.itemType ?? '';
    }

    this.selectedItemType = itemType;
    this.selectedItemData = this.itemData.find(d => d.itemType === itemType);
    this.selectedItemClass = item.itemClass || this.selectedItemData?.itemClass || '';

    if (this.selectedItemData?.isArmour) {
      this.selectedDefenceType = this.itemDataService.getGearDefenceType()
        .find(g => g.name === item.baseType)?.defenceType ?? '';
    } else {
      this.selectedDefenceType = '';
    }

    this.computeBaseTypeOptions();
    this.selectedBaseType = this.baseTypeOptions.some(o => o.name === item.baseType)
      ? item.baseType
      : (this.baseTypeOptions[0]?.name ?? item.baseType);

    this.applyItemProperties(item);
    this.recomputeVisibility();
    this.updatePreview();
  }

  private applyItemProperties(item: GameItem): void {
    this.resetItemProperties();

    const isCurrency = item.rarity === 'Currency'
      || this.itemDataService.isStackOnlyCurrencyItemType(this.selectedItemType);
    if (!isCurrency) {
      this.rarity = (item.rarity as Rarity) ?? Rarity.Normal;
    }
    if (item.itemLevel !== undefined) this.itemLevel = item.itemLevel;
    if (item.quality !== undefined) this.quality = item.quality;
    if (item.corrupted !== undefined) this.corrupted = item.corrupted;
    if (item.stackSize !== undefined) this.stackSize = item.stackSize;

    if (item.identified !== undefined) this.identified = item.identified;
    if (item.rarity === Rarity.Rare && item.name?.trim()) {
      this.rareName = item.name.trim();
    }
    if (item.explicitMods?.length) {
      this.explicitMods = [...item.explicitMods];
      this.explicitModNames = item.explicitModNames?.length
        ? [...item.explicitModNames]
        : item.explicitMods.map(text => this.modOptions.find(m => m.text === text)?.name ?? '');
    }
    if (item.implicitMods?.length) this.implicitModCount = item.implicitMods.length;
    if (item.unidentifiedItemTier !== undefined) this.unidentifiedItemTier = item.unidentifiedItemTier;

    if (item.sockets !== undefined) this.sockets = item.sockets;
    if (item.linkedSockets !== undefined) this.linkedSockets = item.linkedSockets;
    if (item.socketGroup) this.socketGroup = item.socketGroup;
    if (item.memoryStrands !== undefined) this.memoryStrands = item.memoryStrands;

    if (item.hasInfluence) {
      this.hasInfluence = true;
      this.selectedInfluence = item.hasInfluence;
    }
    if (item.fracturedItem) this.fracturedItem = true;
    if (item.synthesisedItem) this.synthesisedItem = true;
    if (item.shaperItem) this.shaperItem = true;
    if (item.elderItem) this.elderItem = true;
    if (item.mirrored) this.mirrored = true;
    if (item.replica) this.replica = true;

    if (item.waystoneTier !== undefined) this.waystoneTier = item.waystoneTier;
    else if (item.mapTier !== undefined) this.waystoneTier = item.mapTier;
    if (item.anyEnchantment || item.hasEnchantment) this.anyEnchantment = true;
    if (item.blightedMap) this.blightedMap = true;
    if (item.uberBlightedMap) this.uberBlightedMap = true;
    if (item.elderMap) this.elderMap = true;
    if (item.shapedMap) this.shapedMap = true;
    if (item.zanaMemory) this.zanaMemory = true;

    if (item.gemLevel !== undefined) this.gemLevel = item.gemLevel;
    if (item.gemQualityType) this.gemQualityType = item.gemQualityType;
    if (typeof item.transfiguredGem === 'string' && item.transfiguredGem) {
      this.transfiguredGem = item.transfiguredGem;
    }

    if (item.enchantmentPassiveNum !== undefined) this.enchantmentPassiveNum = item.enchantmentPassiveNum;
  }

  private buildModOptions(): void {
    const byKey = new Map<string, { text: string; name: string }>();
    for (const m of this.itemDataService.getModData()()) {
      const key = `${m.name}\0${m.text}`;
      if (!byKey.has(key)) {
        byKey.set(key, { text: m.text, name: m.name });
      }
    }
    this.modOptions = [...byKey.entries()].map(([key, mod]) => ({ key, ...mod }));
  }

  private syncRarityFromExplicitMods(): void {
    if (this.rarity === Rarity.Unique) return;

    const modCount = this.explicitMods.length;
    if (modCount === 0) {
      this.rarity = Rarity.Normal;
    } else if (modCount <= 2) {
      this.rarity = Rarity.Magic;
    } else {
      this.rarity = Rarity.Rare;
    }
  }

  private normalizeDesignerSocketGroup(value: string): string {
    return value
      .trim()
      .toUpperCase()
      .replace(/X/g, 'W')
      .replace(/[^RGBW]/g, '');
  }

  private resetItemProperties(): void {
    this.rarity = Rarity.Normal;
    this.itemLevel = undefined;
    this.quality = undefined;
    this.corrupted = undefined;
    this.stackSize = undefined;
    this.identified = undefined;
    this.rareName = '';
    this.explicitMods = [];
    this.explicitModNames = [];
    this.implicitModCount = undefined;
    this.unidentifiedItemTier = undefined;
    this.sockets = undefined;
    this.linkedSockets = undefined;
    this.socketGroup = '';
    this.memoryStrands = undefined;
    this.hasInfluence = false;
    this.selectedInfluence = 'Shaper';
    this.fracturedItem = false;
    this.synthesisedItem = false;
    this.shaperItem = false;
    this.elderItem = false;
    this.mirrored = false;
    this.replica = false;
    this.waystoneTier = undefined;
    this.anyEnchantment = false;
    this.blightedMap = false;
    this.uberBlightedMap = false;
    this.elderMap = false;
    this.shapedMap = false;
    this.zanaMemory = false;
    this.gemLevel = undefined;
    this.gemQualityType = 'Superior';
    this.transfiguredGem = '';
    this.enchantmentPassiveNum = undefined;
    this.modPickerValue = undefined;
  }
}
